/* eslint-disable react/no-array-index-key */
import { useTexture } from '@react-three/drei';
import { LevelType } from 'data/types';
import { useMemo } from 'react';
import { DoubleSide, RepeatWrapping } from 'three';
import { getLevelHeight } from 'utils/3d';
import {
  buildFootprintsWallSegments,
  createFootprintFloorGeometry,
  FOOTPRINT_FLOOR_Y,
  FOOTPRINT_WALL_THICKNESS,
  getLevelSpacePolygonsFeet,
  getUncoveredFootprintsFloorRegions
} from 'utils/3d-footprint-walls';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import floorTexture from './assets/floor.jpg';
import wallTexture from './assets/wall.jpg';

type FootprintWalls3DProps = Readonly<{
  level: LevelType;
  yOffset: number;
  opacity: number;
}>;

export default function FootprintWalls3D({
  level,
  yOffset,
  opacity
}: FootprintWalls3DProps) {
  const [wall, floor] = useTexture([wallTexture, floorTexture]);
  wall.wrapS = RepeatWrapping;
  wall.wrapT = RepeatWrapping;
  floor.wrapS = RepeatWrapping;
  floor.wrapT = RepeatWrapping;

  const wallHeight = getLevelHeight(level.type);
  const footprints = getEffectiveFootprintPolygons(level);

  const spacePolygons = useMemo(
    () => getLevelSpacePolygonsFeet(level),
    [level]
  );

  const segments = useMemo(
    () => buildFootprintsWallSegments(footprints, spacePolygons),
    [footprints, spacePolygons]
  );

  const floorGeometries = useMemo(
    () =>
      getUncoveredFootprintsFloorRegions(footprints, spacePolygons).map(
        (region) => createFootprintFloorGeometry(region)
      ),
    [footprints, spacePolygons]
  );

  if (segments.length === 0 && floorGeometries.length === 0) {
    return null;
  }
  if (opacity <= 0) {
    return null;
  }

  return (
    <group position={[0, yOffset, 0]}>
      {floorGeometries.map((geometry, index) => {
        const floorClone = floor.clone();
        return (
          <mesh
            key={`footprint-floor-${index}`}
            position={[0, FOOTPRINT_FLOOR_Y, 0]}
            rotation={[-Math.PI / 2, 0, 0]}
            renderOrder={getOpacityRenderOrder(opacity)}
          >
            <primitive object={geometry} attach='geometry' />
            <meshStandardMaterial
              map={floorClone}
              side={DoubleSide}
              {...getOpacityMaterialProps(opacity)}
            />
          </mesh>
        );
      })}
      {segments.map((segment, index) => {
        const wallTextureClone = wall.clone();
        wallTextureClone.repeat.set(segment.length * 0.5, wallHeight * 0.5);

        return (
          <mesh
            key={`footprint-wall-${index}`}
            position={[segment.centerX, wallHeight / 2, segment.centerZ]}
            rotation={[0, segment.angle, 0]}
            renderOrder={getOpacityRenderOrder(opacity)}
          >
            <boxGeometry
              args={[segment.length, wallHeight, FOOTPRINT_WALL_THICKNESS]}
            />
            <meshStandardMaterial
              map={wallTextureClone}
              {...getOpacityMaterialProps(opacity)}
            />
          </mesh>
        );
      })}
    </group>
  );
}
